import type { HomeDashboardData } from '@/lib/types';

function linePath(points: Array<{ x: number; y: number }>) {
  if (points.length === 0) {
    return '';
  }

  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
}

function areaPath(points: Array<{ x: number; y: number }>, bottomY: number) {
  if (points.length === 0) {
    return '';
  }

  const start = points[0];
  const end = points[points.length - 1];

  return [
    `M ${start.x.toFixed(2)} ${bottomY.toFixed(2)}`,
    `L ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
    ...points.slice(1).map((point) => `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`),
    `L ${end.x.toFixed(2)} ${bottomY.toFixed(2)}`,
    'Z',
  ].join(' ');
}

export function TrendChart({
  data,
  primaryKey = 'revenue',
  secondaryKey = 'bookings',
}: {
  data: HomeDashboardData['weekly_trend'];
  primaryKey?: 'revenue' | 'bookings';
  secondaryKey?: 'revenue' | 'bookings';
}) {
  const width = 1000;
  const height = 320;
  const paddingX = 28;
  const paddingY = 28;
  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingY * 2;

  const primaryValues = data.map((entry) => Number(entry[primaryKey]) || 0);
  const secondaryValues = data.map((entry) => Number(entry[secondaryKey]) || 0);
  const primaryMax = Math.max(1, ...primaryValues);
  const secondaryMax = Math.max(1, ...secondaryValues);

  const pointsPrimary = data.map((entry, index) => {
    const ratio = data.length > 1 ? index / (data.length - 1) : 0.5;
    const x = paddingX + plotWidth * ratio;
    const y = paddingY + plotHeight - (plotHeight * ((Number(entry[primaryKey]) || 0) / primaryMax));

    return { x, y };
  });

  const pointsSecondary = data.map((entry, index) => {
    const ratio = data.length > 1 ? index / (data.length - 1) : 0.5;
    const x = paddingX + plotWidth * ratio;
    const y = paddingY + plotHeight - (plotHeight * ((Number(entry[secondaryKey]) || 0) / secondaryMax));

    return { x, y };
  });

  const bottomY = paddingY + plotHeight;
  const primaryPath = linePath(pointsPrimary);
  const primaryArea = areaPath(pointsPrimary, bottomY);
  const secondaryPath = linePath(pointsSecondary);

  const gridLines = [0.2, 0.4, 0.6, 0.8];

  return (
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Weekly revenue trend">
      <defs>
        <linearGradient id="primaryFill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.36" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="secondaryFill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="var(--info)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--info)" stopOpacity="0.02" />
        </linearGradient>
        <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              0 0 0 12 -4"
          />
        </filter>
      </defs>

      {gridLines.map((ratio, index) => {
        const y = paddingY + plotHeight - plotHeight * ratio;
        return <line key={index} x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="var(--border)" />;
      })}

      <path d={secondaryPath} fill="none" stroke="var(--info)" strokeOpacity="0.45" strokeWidth="4" />
      <path d={primaryArea} fill="url(#primaryFill)" />
      <path d={primaryPath} fill="none" stroke="var(--accent)" strokeWidth="4.5" filter="url(#glow)" />

      <path d={secondaryPath} fill="none" stroke="var(--info)" strokeWidth="2.5" />

      {pointsPrimary.map((point, index) => (
        <circle key={`primary-${index}`} cx={point.x} cy={point.y} r="4.5" fill="var(--accent)" stroke="var(--card)" strokeWidth="2" />
      ))}

      {pointsSecondary.map((point, index) => (
        <circle key={`secondary-${index}`} cx={point.x} cy={point.y} r="3.5" fill="var(--info)" stroke="var(--card)" strokeWidth="2" />
      ))}

      {data.map((entry, index) => {
        const x = paddingX + plotWidth * (data.length > 1 ? index / (data.length - 1) : 0.5);
        return (
          <text key={`${entry.day}-${index}`} x={x} y={height - 8} textAnchor="middle" fill="var(--text-muted)" fontSize="12">
            {entry.day}
          </text>
        );
      })}
    </svg>
  );
}
